import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canReviewOffCatalog, canPromoteToCatalog } from '../../lib/permissions';
import CatalogPromotionModal from '../../components/inventory/CatalogPromotionModal';
import OnboardingCallout from '../../components/OnboardingCallout';
import {
  IconSparkles,
  IconAlertTriangle,
  IconArrowBackUp,
} from '@tabler/icons-react';

// Issue #243. Catalog promotion had no home of its own: its only reachable entry
// point was the receive sheet, which #249 showed disappears the moment a delivery
// satisfies every line — the common case at D&R, since most orders arrive in one
// delivery. This page is that home. It resolves #249 by construction, because
// promotion no longer depends on a transaction screen being reachable.
//
// Competitive position, researched at 74k7: neither SAP nor Coupa has a
// promote-to-catalog action anywhere in requisition, order or goods receipt.
// Catalog creation is master-data governance with its own screen, queue and owner.

// A line is only worth cataloguing once the company actually committed to buying
// it, which is also the point at which purchasing has created the provisional
// vendor_items row this page pre-fills from. Verified against live data at 74n:
// the single `denied` off-catalog line in the database carries no provisional
// record and must never appear here. `closed` is included by reasoning rather
// than measurement — no closed off-catalog line exists yet.
const REVIEWABLE_STATUSES = ['ordered', 'received', 'closed'];

const AMBER_RAIL = '#EF9F27';
const AMBER_TEXT = '#9A5F00';
const AMBER_BG = '#FFF8EC';
const GREEN = '#1D9E75';

// off_catalog_vendor_name is free text the requester typed and carries junk in
// live data — the literal strings "None" and "null" both appear. Treated as
// absent rather than rendered, so a card never reads "Vendor: None".
const EMPTY_VENDOR_TEXT = ['none', 'null', 'n/a', '-', ''];

function suggestedVendorName(row) {
  const raw = (row?.off_catalog_vendor_name || '').trim();
  if (EMPTY_VENDOR_TEXT.includes(raw.toLowerCase())) return null;
  return raw;
}

// The provisional row stores what purchasing paid per pack. Unit cost is that
// divided by pack size — the same derivation CatalogPromotionModal performs when
// it pre-fills, kept identical here so the queue and the modal never disagree.
function unitCostFrom(provisional) {
  if (!provisional) return null;
  const price = parseFloat(provisional.vendor_price);
  if (!Number.isFinite(price)) return null;
  const packs = parseInt(provisional.pack_qty, 10);
  if (Number.isFinite(packs) && packs > 0) return price / packs;
  return price;
}

function money(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  return '$' + n.toFixed(2);
}

function reviewDate(request) {
  const stamp = request?.received_at || request?.ordered_at || request?.created_at;
  if (!stamp) return null;
  return new Date(stamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function qtyLabel(row) {
  // qty_received is null until a delivery lands, so an ordered-but-not-received
  // line legitimately has no received figure. Show what was asked for instead of
  // an em dash with no context.
  if (row.qty_received !== null && row.qty_received !== undefined) {
    return { value: Number(row.qty_received), word: 'received' };
  }
  return { value: Number(row.qty_requested), word: 'ordered' };
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function VendorCell({ row, provisional, vendorNames }) {
  const unit = unitCostFrom(provisional);
  const name = provisional?.vendor_id ? vendorNames[provisional.vendor_id] : null;
  const suggested = suggestedVendorName(row);

  if (!provisional) {
    return (
      <div>
        <div style={{ color: suggested ? '#0F0F0F' : '#9A9A9A', fontSize: 13 }}>
          {suggested || 'No vendor on file'}
        </div>
        <div style={{ fontSize: 12, color: AMBER_TEXT }}>No order record</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ color: '#0F0F0F', fontSize: 13 }}>{name || suggested || 'Unnamed vendor'}</div>
      <div style={{ fontSize: 12, color: unit === null ? AMBER_TEXT : '#7A7A7A' }}>
        {unit === null
          ? 'No price on order'
          : `${money(parseFloat(provisional.vendor_price))} / ${provisional.pack_qty || 1} ${provisional.pack_unit || 'pack'} → ${money(unit)} each`}
      </div>
    </div>
  );
}

function ActionButtons({ row, dismissed, busy, canPromote, onPromote, onDismiss, onRestore, compact }) {
  if (dismissed) {
    return (
      <button
        onClick={() => onRestore(row)}
        disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: 'none', border: '1px solid #E0E0E0', borderRadius: 9,
          padding: compact ? '9px 14px' : '6px 12px',
          fontSize: compact ? 14 : 12, fontWeight: 600,
          color: busy ? '#9A9A9A' : '#5C5C5C',
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
      >
        <IconArrowBackUp size={14} /> Restore to queue
      </button>
    );
  }

  // 74u. Add to catalog is admin/executive only; working the queue is not.
  // Purchasing and branch managers keep the row, the detail and Dismiss —
  // they lose only the act of creating permanent company-wide master data.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: compact ? 'stretch' : 'flex-end' }}>
      {canPromote && (
      <button
        onClick={() => onPromote(row)}
        disabled={busy}
        style={{
          flex: compact ? 1 : 'none',
          background: busy ? '#E0E0E0' : GREEN,
          border: 'none', borderRadius: 9,
          padding: compact ? '10px 0' : '6px 13px',
          fontSize: compact ? 14 : 12, fontWeight: 700,
          color: busy ? '#9A9A9A' : 'white',
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
      >
        Add to catalog
      </button>
      )}
      <button
        onClick={() => onDismiss(row)}
        disabled={busy}
        style={{
          background: 'none', border: 'none', padding: compact ? '10px 4px' : '6px 4px',
          fontSize: compact ? 13 : 12, fontWeight: 600,
          color: busy ? '#C0C0C0' : '#7A7A7A',
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
      >
        Dismiss
      </button>
    </div>
  );
}

function MobileCard({ row, provisional, vendorNames, branchNames, dismissed, busy, canPromote, onPromote, onDismiss, onRestore }) {
  const qty = qtyLabel(row);
  const req = row.material_requests;
  const when = reviewDate(req);

  return (
    <div style={{
      background: 'white', border: '1px solid #F0F0F0',
      borderLeft: `3px solid ${dismissed ? '#D0D0D0' : AMBER_RAIL}`,
      borderRadius: 0, padding: '12px 13px', marginBottom: 10,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F', lineHeight: 1.35 }}>
        {row.off_catalog_description || 'Untitled off-catalog item'}
      </div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 3 }}>
        {qty.value} {row.off_catalog_uom || 'unit'} {qty.word} · {branchNames[req?.branch_id] || 'Unknown branch'}
      </div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>
        <span style={{ fontFamily: 'monospace' }}>{req?.request_number || '—'}</span>
        {when ? ` · ${when}` : ''}
      </div>
      <div style={{ marginTop: 6 }}>
        <VendorCell row={row} provisional={provisional} vendorNames={vendorNames} />
      </div>
      <div style={{ marginTop: 11 }}>
        <ActionButtons
          row={row}
          dismissed={dismissed}
          busy={busy}
          canPromote={canPromote}
          onPromote={onPromote}
          onDismiss={onDismiss}
          onRestore={onRestore}
          compact
        />
      </div>
    </div>
  );
}

function DesktopRow({ row, provisional, vendorNames, branchNames, dismissed, busy, canPromote, onPromote, onDismiss, onRestore }) {
  const qty = qtyLabel(row);
  const req = row.material_requests;
  const when = reviewDate(req);

  return (
    <tr style={{ borderBottom: '1px solid #F0F0F0' }}>
      <td style={{ padding: '11px 10px 11px 0' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>
          {row.off_catalog_description || 'Untitled off-catalog item'}
        </div>
        <div style={{ fontSize: 12, color: '#7A7A7A' }}>
          {row.off_catalog_uom || 'unit'} · {branchNames[req?.branch_id] || 'Unknown branch'}
        </div>
      </td>
      <td style={{ padding: '11px 10px 11px 0' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#0F0F0F' }}>{req?.request_number || '—'}</div>
        <div style={{ fontSize: 12, color: '#7A7A7A', textTransform: 'capitalize' }}>
          {req?.status || '—'}{when ? ` · ${when}` : ''}
        </div>
      </td>
      <td style={{ padding: '11px 10px 11px 0', textAlign: 'right' }}>
        <div style={{ fontSize: 13, color: '#0F0F0F' }}>{qty.value}</div>
        <div style={{ fontSize: 11, color: '#9A9A9A' }}>{qty.word}</div>
      </td>
      <td style={{ padding: '11px 10px 11px 0' }}>
        <VendorCell row={row} provisional={provisional} vendorNames={vendorNames} />
      </td>
      <td style={{ padding: '11px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
        <ActionButtons
          row={row}
          dismissed={dismissed}
          busy={busy}
          canPromote={canPromote}
          onPromote={onPromote}
          onDismiss={onDismiss}
          onRestore={onRestore}
        />
      </td>
    </tr>
  );
}

function EmptyState({ tab }) {
  const copy = tab === 'dismissed'
    ? { head: 'Nothing dismissed', body: 'Items you decide should not become catalog entries will collect here.' }
    : { head: 'Nothing waiting', body: 'Every off-catalog item that has been ordered or received is either in the catalog or dismissed.' };

  return (
    <div style={{ textAlign: 'center', padding: '48px 20px' }}>
      <div style={{
        width: 52, height: 52, borderRadius: '50%', background: AMBER_BG,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
      }}>
        <IconSparkles size={24} color={AMBER_RAIL} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F', marginBottom: 4 }}>{copy.head}</div>
      <div style={{ fontSize: 13, color: '#7A7A7A', maxWidth: 380, margin: '0 auto' }}>{copy.body}</div>
    </div>
  );
}

export default function OffCatalogReviewPage({ onCountsChanged }) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = useAuth();
  const isMobile = useIsMobile();
  const canReview = canReviewOffCatalog({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // #448 — isManager added: promote and add are one rule now, manager or higher.
  // #422/78t -- `profile` added; see the note in permissions.js. Without it
  // a branch manager cannot promote, which is the whole point of this page.
  const canPromote = canPromoteToCatalog({ isAdmin, isManager, isExecutive, profile });
  // Callouts gate Wave 2 — inv_offcatalog_first, with TWO corrections to the
  // gate made by reading this screen rather than the mockup.
  //
  // The gate's premise is "Promote and Dismiss look like a pair". They do not.
  // The button is labelled "Add to catalog", is solid green and primary, and
  // Dismiss is plain grey text beside it — the hierarchy already separates them.
  // What is genuinely unsaid is that one writes permanent company-wide master
  // data and the other is undoable, so the copy says that instead.
  //
  // And the gate's role column says "Purchasing +", which would show this to
  // people who cannot see the button at all: promotion is admin/executive only
  // (74u), while working the queue is not. Gated on canPromote, so the tip never
  // describes a control that is not on the reader's screen. That is D4.
  const offCatalogTitleRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [provisionals, setProvisionals] = useState({});
  const [vendorNames, setVendorNames] = useState({});
  const [branchNames, setBranchNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('waiting');
  const [busyId, setBusyId] = useState(null);

  const [promotionRow, setPromotionRow] = useState(null);

  // Deliberately not branch-scoped. Catalog entries are company-wide master data,
  // and purchasing already spans branches by design — Jennifer runs purchasing
  // from Houston for requests that originate at Dallas. Branch is shown per row
  // rather than filtered on. Do not add a branch gate here.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    // is_off_catalog records how the line was ordered and never changes;
    // item_id records whether it resolves to a catalog item today. That
    // separation is #244's fix, and it is why a promoted line leaves this queue
    // on its own with no extra bookkeeping. Both filters are base-table columns,
    // and request status is filtered client-side below rather than through the
    // embedded relation — PostgREST returns empty silently on nested filters and
    // the row count here is small enough that there is nothing to gain.
    const { data, error: fetchErr } = await supabase
      .from('material_request_items')
      .select(`
        id, qty_requested, qty_received, off_catalog_description, off_catalog_uom,
        off_catalog_vendor_name, off_catalog_vendor_id, item_id, is_off_catalog,
        catalog_review_dismissed_at, catalog_review_dismissed_by,
        material_requests (
          id, request_number, status, branch_id, vendor_id,
          created_at, ordered_at, received_at
        )
      `)
      .eq('is_off_catalog', true)
      .is('item_id', null);

    if (fetchErr) {
      setError('Could not load off-catalog items: ' + fetchErr.message);
      setLoading(false);
      return;
    }

    const reviewable = (data || []).filter(
      r => REVIEWABLE_STATUSES.includes(r.material_requests?.status)
    );
    setRows(reviewable);

    // Same lookup fetchProvisionalVendorItem() performs for a single line,
    // batched for a list. Kept on the same is_provisional filter so the queue
    // and the receive sheet resolve the identical row.
    const itemIds = reviewable.map(r => r.id);
    if (itemIds.length) {
      const { data: vi } = await supabase
        .from('vendor_items')
        .select('id, vendor_id, vendor_price, pack_qty, pack_unit, vendor_item_number, provisional_request_item_id')
        .eq('is_provisional', true)
        .in('provisional_request_item_id', itemIds);

      const byItem = {};
      (vi || []).forEach(v => { byItem[v.provisional_request_item_id] = v; });
      setProvisionals(byItem);
    } else {
      setProvisionals({});
    }

    const { data: vens } = await supabase.from('inventory_vendors').select('id, name');
    const vmap = {};
    (vens || []).forEach(v => { vmap[v.id] = v.name; });
    setVendorNames(vmap);

    // Read branch names from the branches table rather than adding a fourteenth
    // hardcoded BRANCHES array — see #252.
    const { data: brs } = await supabase.from('branches').select('id, name');
    const bmap = {};
    (brs || []).forEach(b => { bmap[b.id] = b.name; });
    setBranchNames(bmap);

    // Keep the sidebar badge in step with what this page is showing. Called from
    // load() rather than from each action so there is one place it can go stale.
    onCountsChanged?.();

    setLoading(false);
  }, [onCountsChanged]);

  useEffect(() => { load(); }, [load]);

  const waiting = rows.filter(r => !r.catalog_review_dismissed_at);
  const dismissed = rows.filter(r => r.catalog_review_dismissed_at);
  const visible = tab === 'dismissed' ? dismissed : waiting;

  async function handleDismiss(row) {
    if (!profile?.id) return;
    setBusyId(row.id);
    setError('');
    const { error: updErr } = await supabase
      .from('material_request_items')
      .update({
        catalog_review_dismissed_at: new Date().toISOString(),
        catalog_review_dismissed_by: profile.id,
      })
      .eq('id', row.id);
    setBusyId(null);
    if (updErr) {
      setError('Could not dismiss that item: ' + updErr.message);
      return;
    }
    await load();
  }

  async function handleRestore(row) {
    setBusyId(row.id);
    setError('');
    const { error: updErr } = await supabase
      .from('material_request_items')
      .update({ catalog_review_dismissed_at: null, catalog_review_dismissed_by: null })
      .eq('id', row.id);
    setBusyId(null);
    if (updErr) {
      setError('Could not restore that item: ' + updErr.message);
      return;
    }
    await load();
  }

  if (!canReview) {
    return (
      <div style={{ padding: 32, color: '#7A7A7A', fontSize: 14 }}>
        You do not have access to off-catalog review.
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '16px 12px 90px' : '24px 28px' }}>

      <OnboardingCallout
        calloutKey="inv_offcatalog_first"
        text="Add to catalog creates a permanent company-wide item. Dismiss only clears the row — you can restore it."
        anchorRef={isMobile ? null : offCatalogTitleRef}
        isMobile={isMobile}
        show={canPromote}
      />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconSparkles size={isMobile ? 18 : 20} color={AMBER_TEXT} />
            <span ref={offCatalogTitleRef} style={{ fontSize: isMobile ? 17 : 20, fontWeight: 800, color: '#0F0F0F' }}>
              Off-catalog review
            </span>
            {waiting.length > 0 && (
              <span style={{
                background: AMBER_BG, color: AMBER_TEXT, fontSize: 12, fontWeight: 700,
                padding: '2px 9px', borderRadius: 20,
              }}>
                {waiting.length} waiting
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#7A7A7A', marginTop: 4 }}>
            Items bought off-catalog that are not in the catalog yet.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {[['waiting', `Waiting (${waiting.length})`], ['dismissed', `Dismissed (${dismissed.length})`]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 9,
                background: tab === id ? '#F5F5F3' : 'none',
                border: tab === id ? '1px solid #D0D0D0' : '1px solid transparent',
                color: tab === id ? '#0F0F0F' : '#7A7A7A',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{
          background: '#FFEBEE', color: '#A32D2D', borderRadius: 9, padding: '9px 13px',
          fontSize: 12, marginTop: 14, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <IconAlertTriangle size={14} /> {error}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>
            Loading off-catalog items...
          </div>
        ) : visible.length === 0 ? (
          <EmptyState tab={tab} />
        ) : isMobile ? (
          visible.map(row => (
            <MobileCard
              key={row.id}
              row={row}
              provisional={provisionals[row.id]}
              vendorNames={vendorNames}
              branchNames={branchNames}
              dismissed={tab === 'dismissed'}
              busy={busyId === row.id}
              canPromote={canPromote}
              onPromote={setPromotionRow}
              onDismiss={handleDismiss}
              onRestore={handleRestore}
            />
          ))
        ) : (
          <div style={{ background: 'white', border: '1px solid #F0F0F0', borderRadius: 12, padding: '14px 18px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
                  {['ITEM', 'REQUEST', 'QTY', 'VENDOR / PRICE', ''].map((h, i) => (
                    <th
                      key={h || i}
                      style={{
                        textAlign: i === 2 ? 'right' : 'left',
                        padding: '0 10px 7px 0',
                        fontSize: 11, fontWeight: 700, color: '#9A9A9A', letterSpacing: '0.03em',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(row => (
                  <DesktopRow
                    key={row.id}
                    row={row}
                    provisional={provisionals[row.id]}
                    vendorNames={vendorNames}
                    branchNames={branchNames}
                    dismissed={tab === 'dismissed'}
                    busy={busyId === row.id}
                    canPromote={canPromote}
                    onPromote={setPromotionRow}
                    onDismiss={handleDismiss}
                    onRestore={handleRestore}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {promotionRow && (
        <CatalogPromotionModal
          key={promotionRow.id}
          matchedRequest={promotionRow.material_requests}
          requestItem={promotionRow}
          provisionalVendorItem={provisionals[promotionRow.id] || null}
          profile={profile}
          onClose={() => setPromotionRow(null)}
          onPromoted={() => { setPromotionRow(null); load(); }}
        />
      )}
    </div>
  );
}
